import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const TTL_MS = 5 * 60 * 1000; // 5 minutes

// Module-level cache — shared across all component instances in the same session
let _cache = null;
let _cacheExpiry = 0;
let _pending = null; // deduplicate concurrent fetches

export function invalidateUsersCache() {
  _cache = null;
  _cacheExpiry = 0;
  _pending = null;
}

async function fetchUsers() {
  if (_cache && Date.now() < _cacheExpiry) return _cache;
  if (_pending) return _pending;

  _pending = getDocs(collection(db, 'users')).then(snap => {
    _cache = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    _cacheExpiry = Date.now() + TTL_MS;
    _pending = null;
    return _cache;
  }).catch(err => {
    _pending = null;
    throw err;
  });

  return _pending;
}

export function useUsers() {
  const [users, setUsers] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache || Date.now() >= _cacheExpiry);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (_cache && Date.now() < _cacheExpiry) {
      setUsers(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchUsers()
      .then(data => { if (mounted) { setUsers(data); setLoading(false); } })
      .catch(err => { if (mounted) { setError(err); setLoading(false); } });
    return () => { mounted = false; };
  }, []);

  return { users, loading, error, refetch: () => { invalidateUsersCache(); } };
}
